import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Button, message, Upload, Divider, InputNumber, Card, Row, Col, Typography, Switch } from "antd";
import { UploadOutlined, DeleteOutlined, LockOutlined, DollarOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as accountSettingsService from "../services/accountSettingsService";
import config from "../config";
import { resolveLogoUrl } from "../utils/resolveLogoUrl";

const { Text } = Typography;

// Generate unique ID for new tiers
const generateTierId = () => `tier_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const AccountSettingsModal = ({ open, onClose, currentSettings, onPasswordChangeClick }) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [pricingTiers, setPricingTiers] = useState([]);
  const [enableTaxOption, setEnableTaxOption] = useState(true);

  // Parse existing tiers from settings
  useEffect(() => {
    if (open && currentSettings) {
      form.setFieldsValue({
        companyName: currentSettings.companyName,
        email: currentSettings.email || "",
        phone: currentSettings.phone || "",
        address: currentSettings.address || "",
        taxRate: currentSettings.taxRate || 6,
      });
      setLogoUrl(currentSettings.logoUrl || null);
      setEnableTaxOption(currentSettings.enableTaxOption !== false); // default to true

      // Load pricing tiers from JSON or legacy fields
      let tiers = [];
      if (currentSettings.pricingTiersJson) {
        try {
          tiers = JSON.parse(currentSettings.pricingTiersJson);
        } catch (e) {
          console.error("Failed to parse pricing tiers JSON:", e);
        }
      }

      // If no JSON tiers, migrate from legacy fields
      if (tiers.length === 0) {
        if (currentSettings.tier1Qty && currentSettings.tier1Discount) {
          tiers.push({
            id: "tier1",
            name: currentSettings.tier1Name || "Tier 1",
            minQty: currentSettings.tier1Qty,
            discount: currentSettings.tier1Discount
          });
        }
        if (currentSettings.tier2Qty && currentSettings.tier2Discount) {
          tiers.push({
            id: "tier2",
            name: currentSettings.tier2Name || "Tier 2",
            minQty: currentSettings.tier2Qty,
            discount: currentSettings.tier2Discount
          });
        }
        if (currentSettings.tier3Qty && currentSettings.tier3Discount) {
          tiers.push({
            id: "tier3",
            name: currentSettings.tier3Name || "Tier 3",
            minQty: currentSettings.tier3Qty,
            discount: currentSettings.tier3Discount
          });
        }
      }

      setPricingTiers(tiers);
    }
  }, [open, currentSettings, form]);

  const updateMutation = useMutation({
    mutationFn: accountSettingsService.updateAccountSettings,
    onSuccess: (data) => {
      // Invalidate all accountSettings queries (with or without token in key)
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === "accountSettings"
      });
      message.success("Account settings updated successfully!");
      console.log("Settings saved successfully:", data);
      onClose();
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message || error.message || "Failed to update account settings";
      message.error(errorMessage);
      console.error("Update error:", error.response?.data || error);
    },
  });

  const handleLogoUpload = async (file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${config.SPRING_API_BASE}/api/logo/upload`, {
        method: "POST",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Upload failed");
      }

      const data = await response.json();
      setLogoUrl(data.url);
      message.success("Logo uploaded successfully!");
    } catch (error) {
      message.error(error.message || "Failed to upload logo");
      console.error("Upload error:", error);
    } finally {
      setUploading(false);
    }

    return false;
  };

  const handleLogoRemove = async () => {
    // Extract the filename from the stored URL and ask the backend to delete the file.
    const current = logoUrl;
    setLogoUrl(null);

    if (!current) {
      message.success("Logo removed");
      return;
    }

    try {
      // logoUrl is a path like /uploads/logos/<uuid>.png — grab the last segment
      const filename = String(current).split("/").filter(Boolean).pop();
      if (!filename) {
        message.success("Logo removed");
        return;
      }

      const token = localStorage.getItem("auth_token");
      const res = await fetch(
        `${config.SPRING_API_BASE}/api/logo?filename=${encodeURIComponent(filename)}`,
        {
          method: "DELETE",
          headers: { Authorization: token ? `Bearer ${token}` : "" },
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Backend did not delete the file");
      }
      message.success("Logo removed");
    } catch (error) {
      console.error("Logo delete error:", error);
      message.warning("Logo cleared from settings, but the file may still be on the server.");
    }
  };

  const handleAddTier = () => {
    const newTier = {
      id: generateTierId(),
      name: `Tier ${pricingTiers.length + 1}`,
      minQty: null,
      discount: null
    };
    setPricingTiers([...pricingTiers, newTier]);
  };

  const handleRemoveTier = (tierId) => {
    setPricingTiers(pricingTiers.filter(t => t.id !== tierId));
  };

  const handleTierChange = (tierId, field, value) => {
    setPricingTiers(pricingTiers.map(t =>
      t.id === tierId ? { ...t, [field]: value } : t
    ));
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Pricing tier sanity checks. Silently dropping incomplete rows is fine,
      // but bogus configs (negative qty, discount > 99%, duplicate minQty, or
      // a lower-qty tier with a larger discount than a higher-qty tier) would
      // produce surprising prices — reject those outright with a clear message.
      const validTiers = pricingTiers
        .filter(t => t.minQty !== null && t.minQty !== undefined && t.minQty !== ""
                  && t.discount !== null && t.discount !== undefined && t.discount !== "")
        .map(t => ({ ...t, minQty: Number(t.minQty), discount: Number(t.discount) }))
        .sort((a, b) => a.minQty - b.minQty);

      for (const t of validTiers) {
        if (!Number.isFinite(t.minQty) || t.minQty < 0) {
          message.error(`Tier "${t.name || "(unnamed)"}" has an invalid minimum quantity`);
          return;
        }
        if (!Number.isFinite(t.discount) || t.discount < 0 || t.discount >= 100) {
          message.error(`Tier "${t.name || "(unnamed)"}" discount must be between 0 and 99`);
          return;
        }
      }
      for (let i = 1; i < validTiers.length; i++) {
        if (validTiers[i].minQty === validTiers[i - 1].minQty) {
          message.error(`Two tiers can't share the same minimum quantity (${validTiers[i].minQty})`);
          return;
        }
        if (validTiers[i].discount < validTiers[i - 1].discount) {
          message.error(
            `Tier "${validTiers[i].name || "(unnamed)"}" has a smaller discount than the lower tier — higher quantities should get a larger or equal discount`
          );
          return;
        }
      }

      // Prepare the payload with explicit type handling
      const payload = {
        companyName: values.companyName,
        phone: values.phone || null,
        address: values.address || null,
        logoUrl: logoUrl,
        taxRate: (values.taxRate !== null && values.taxRate !== undefined && values.taxRate !== '')
          ? Number(values.taxRate)
          : null,
        enableTaxOption: enableTaxOption,
        pricingTiers: validTiers.map(tier => ({
          id: tier.id,
          name: tier.name,
          minQty: Number(tier.minQty),
          discount: Number(tier.discount)
        })),
      };

      console.log("Saving account settings:", payload);

      setLoading(true);
      await updateMutation.mutateAsync(payload);
    } catch (error) {
      if (error.errorFields) {
        // Form validation error
        console.error("Form validation failed:", error.errorFields);
      } else {
        console.error("Save failed:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Account Settings"
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
          style={{ background: '#1f2937' }}
        >
          Save Changes
        </Button>,
      ]}
      width={700}
      styles={{ body: { maxHeight: "70vh", overflowY: "auto" } }}
    >
      <Form
        form={form}
        layout="vertical"
        className="mt-4"
      >
        <Form.Item
          label="Company Name"
          name="companyName"
          rules={[{ required: true, message: "Company name is required" }]}
        >
          <Input size="large" placeholder="Enter company name" />
        </Form.Item>

        <Form.Item
          label="Email"
          name="email"
          rules={[{ type: "email", message: "Please enter a valid email" }]}
        >
          <Input
            size="large"
            placeholder="Enter email address"
            disabled
            style={{ backgroundColor: "#f5f5f5", cursor: "not-allowed" }}
          />
        </Form.Item>
        <div style={{ fontSize: "12px", color: "#8c8c8c", marginTop: "-16px", marginBottom: "16px" }}>
          Email is automatically synced with your account and cannot be changed here
        </div>

        <Form.Item label="Phone" name="phone">
          <Input size="large" placeholder="Enter phone number" />
        </Form.Item>

        <Form.Item label="Address" name="address">
          <Input.TextArea
            size="large"
            rows={3}
            placeholder="Enter business address"
          />
        </Form.Item>

        <Form.Item label="Company Logo">
          <div style={{ marginTop: 8 }}>
            {logoUrl ? (
              <div style={{
                border: "1px solid #d9d9d9",
                borderRadius: "8px",
                padding: "16px",
                background: "#fafafa"
              }}>
                <div style={{ marginBottom: 12 }}>
                  <img
                    src={resolveLogoUrl(logoUrl)}
                    alt="Company Logo"
                    style={{
                      maxWidth: "200px",
                      maxHeight: "100px",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                </div>
                <Button
                  icon={<DeleteOutlined />}
                  onClick={handleLogoRemove}
                  danger
                  size="small"
                >
                  Remove Logo
                </Button>
              </div>
            ) : (
              <Upload
                beforeUpload={handleLogoUpload}
                accept="image/*"
                showUploadList={false}
                disabled={uploading}
              >
                <Button
                  icon={<UploadOutlined />}
                  loading={uploading}
                  size="large"
                >
                  {uploading ? "Uploading..." : "Upload Logo"}
                </Button>
              </Upload>
            )}
            <div style={{ marginTop: 8, fontSize: 12, color: "#8c8c8c" }}>
              Recommended: PNG or JPG, max 5MB. Will be used on invoices and contracts.
            </div>
          </div>
        </Form.Item>

        <Divider orientation="left">
          <DollarOutlined style={{ marginRight: 8 }} />
          Wholesale Pricing Tiers
        </Divider>

        <div style={{ marginBottom: 16 }}>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Configure volume discount tiers for wholesale orders. Add as many tiers as you need.
          </Text>
        </div>

        {/* Enable Tax Option Toggle */}
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px",
          backgroundColor: "#fafafa",
          borderRadius: "8px",
          marginBottom: "16px"
        }}>
          <div>
            <Text strong>Enable Tax Option in Orders</Text>
            <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
              When enabled, orders will show a toggle to include/exclude tax
            </div>
          </div>
          <Switch
            checked={enableTaxOption}
            onChange={(checked) => setEnableTaxOption(checked)}
          />
        </div>

        {/* Tax Rate - only show if tax option is enabled */}
        {enableTaxOption && (
          <>
            <Form.Item label="Default Tax Rate (%)" name="taxRate">
              <InputNumber
                min={0}
                max={100}
                precision={2}
                style={{ width: "100%" }}
                placeholder="6"
              />
            </Form.Item>
            <div style={{ fontSize: "12px", color: "#8c8c8c", marginTop: "-16px", marginBottom: "16px" }}>
              This rate is used when "Include Tax" is enabled on orders
            </div>
          </>
        )}

        <div style={{ marginBottom: 8 }}>
          <Text strong style={{ fontSize: 14 }}>Volume Discount Tiers</Text>
          <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
            (sorted by minimum quantity)
          </Text>
        </div>

        {/* Dynamic Pricing Tiers */}
        {pricingTiers.map((tier, index) => (
          <Card
            key={tier.id}
            size="small"
            style={{ marginBottom: 12 }}
            className="bg-gray-50"
            extra={
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => handleRemoveTier(tier.id)}
              >
                Remove
              </Button>
            }
          >
            <Row gutter={16} align="middle">
              <Col xs={24} sm={8}>
                <div style={{ marginBottom: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Name</Text>
                  <Input
                    value={tier.name}
                    onChange={(e) => handleTierChange(tier.id, 'name', e.target.value)}
                    placeholder="e.g., Bronze"
                    size="small"
                  />
                </div>
              </Col>
              <Col xs={12} sm={8}>
                <div style={{ marginBottom: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Min Quantity</Text>
                  <InputNumber
                    value={tier.minQty}
                    onChange={(value) => handleTierChange(tier.id, 'minQty', value)}
                    min={1}
                    style={{ width: "100%" }}
                    placeholder="e.g., 25"
                    size="small"
                  />
                </div>
              </Col>
              <Col xs={12} sm={8}>
                <div style={{ marginBottom: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Discount (%)</Text>
                  <InputNumber
                    value={tier.discount}
                    onChange={(value) => handleTierChange(tier.id, 'discount', value)}
                    min={0}
                    max={100}
                    precision={2}
                    style={{ width: "100%" }}
                    placeholder="e.g., 10"
                    size="small"
                  />
                </div>
              </Col>
            </Row>
          </Card>
        ))}

        {/* Add Tier Button */}
        <Button
          type="dashed"
          onClick={handleAddTier}
          style={{ width: "100%", marginBottom: 16 }}
          icon={<PlusOutlined />}
        >
          Add Pricing Tier
        </Button>

        {pricingTiers.length === 0 && (
          <div style={{
            padding: 16,
            backgroundColor: "#fafafa",
            borderRadius: 6,
            border: "1px dashed #d9d9d9",
            textAlign: "center",
            marginBottom: 16
          }}>
            <Text type="secondary">
              No pricing tiers configured. Click "Add Pricing Tier" to create volume discounts.
            </Text>
          </div>
        )}

        <div style={{ marginBottom: 16, padding: 12, backgroundColor: "#f6ffed", borderRadius: 6, border: "1px solid #b7eb8f" }}>
          <Text style={{ fontSize: 12, color: "#52c41a" }}>
            <strong>How it works:</strong> When creating a wholesale order, the discount is auto-calculated based on total quantity.
            You can also manually select a tier or enter a custom discount per order.
          </Text>
        </div>

        <Divider />

        <Form.Item label="Password">
          <Button
            icon={<LockOutlined />}
            onClick={onPasswordChangeClick}
            block
            size="large"
          >
            Change Password
          </Button>
          <div style={{ marginTop: 8, fontSize: 12, color: "#8c8c8c" }}>
            Update your password for email/password login
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AccountSettingsModal;
